import { useEffect, useMemo, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useToastNotification } from 'hooks/useToastNotification';
import { useEventSuggestions, useUpdateMapping } from '../../hooks';
import { useWizard } from '../../store';
import { handleFileMissingError } from '../../utils';
import {
	Button,
	Card,
	CardDescription,
	CardTitle,
	ErrorPill,
	Footer,
	Row,
	ScrollContainer,
	Stat,
	SuccessPill,
	Table,
} from '../../TicketImporter.styles';
import { EventCombobox } from '../EventCombobox';

const eventPairKey = (name: string, csvDate: string, csvTime: string): string =>
	`${name.trim().toLowerCase()}||${csvDate.trim()}||${(csvTime || '').trim()}`;

export const EventMappingStep = (): JSX.Element => {
	const history = useHistory();
	const { pathname } = useLocation();
	const { state, dispatch } = useWizard();
	const { showNotification, TYPES } = useToastNotification();

	const isFixed = state.scopeMode === 'fixed';

	// Fixed scope: this step has nothing to do — every row goes to one event.
	useEffect(() => {
		if (isFixed) {
			history.replace(`${pathname}#5`);
		}
	}, [isFixed, history, pathname]);

	const { data, isLoading, refetch, isFetching } = useEventSuggestions(state.importId, {
		onError: (err: any) => {
			if (
				handleFileMissingError(err, {
					notify: showNotification,
					notifyType: TYPES,
					reset: () => dispatch({ type: 'reset' }),
					navigate: (p) => history.replace(p),
					basePath: pathname,
				})
			)
				return;
			const msg = err?.response?.data?.error || 'Could not load event suggestions';
			showNotification({ type: TYPES.ERROR, message: msg });
		},
	});

	const [mapping, setMapping] = useState<Record<string, number>>(state.eventMapping);

	// Seed the local mapping with the backend's auto-suggestions the first
	// time we get data back.
	useEffect(() => {
		if (!data) return;
		setMapping((prev) => {
			const next = { ...prev };
			for (const pair of data.pairs) {
				const key = eventPairKey(pair.event_name, pair.csv_date, pair.csv_time || '');
				if (next[key]) continue; // user already picked or restored from state
				if (pair.suggested_event_id) next[key] = pair.suggested_event_id;
			}
			return next;
		});
	}, [data]);

	const pairs = data?.pairs ?? [];

	const stats = useMemo(() => {
		const total = pairs.length;
		const resolved = pairs.filter((p) => mapping[eventPairKey(p.event_name, p.csv_date, p.csv_time || '')]).length;
		const totalRows = pairs.reduce((sum, p) => sum + p.row_count, 0);
		return { total, resolved, totalRows };
	}, [pairs, mapping]);

	const setPair = (name: string, csvDate: string, csvTime: string, eventId: number | null) => {
		const key = eventPairKey(name, csvDate, csvTime);
		setMapping((prev) => {
			const next = { ...prev };
			if (eventId === null) delete next[key];
			else next[key] = eventId;
			return next;
		});
	};

	const { mutate, isLoading: saving } = useUpdateMapping(state.importId as number, {
		onSuccess: () => {
			dispatch({ type: 'patch', payload: { eventMapping: mapping } });
			history.push(`${pathname}#5`);
		},
		onError: () => showNotification({ type: TYPES.ERROR, message: 'Could not save event mapping' }),
	});

	const onContinue = () => {
		if (stats.resolved < stats.total) {
			showNotification({ type: TYPES.ERROR, message: 'Map every CSV event before continuing' });
			return;
		}
		mutate({
			value_mapping: {
				ticket_type: state.valueMapping.ticket_type || {},
				ticket_type_by_event: state.valueMapping.ticket_type_by_event || {},
				event: mapping,
			},
		});
	};

	if (isFixed) return <div />; // brief flash while the useEffect redirects

	return (
		<>
			<Card>
				<CardTitle>4. Event mapping</CardTitle>
				<CardDescription>
					For each unique event detected in the CSV, pick the corresponding event in our system. We suggest the closest
					match by name and date, but you should review each row before continuing.
				</CardDescription>

				{(isLoading || isFetching) && <p>Scanning the CSV and looking up events…</p>}

				{!isLoading && pairs.length === 0 && (
					<p style={{ color: '#9ca3af' }}>
						No unique (event name, event date) pairs found. Check that your column mapping in step 3 includes both event
						name and event date.
					</p>
				)}

				{pairs.length > 0 && (
					<>
						<Row>
							<Stat>
								<span className="label">Unique events in CSV</span>
								<span className="value">{stats.total}</span>
							</Stat>
							<Stat>
								<span className="label">Mapped</span>
								<span className="value" style={{ color: stats.resolved === stats.total ? '#065f46' : '#92400e' }}>
									{stats.resolved} / {stats.total}
								</span>
							</Stat>
							<Stat>
								<span className="label">CSV rows covered</span>
								<span className="value">{stats.totalRows.toLocaleString()}</span>
							</Stat>
						</Row>

						<ScrollContainer style={{ maxHeight: 520 }}>
							<Table>
								<thead>
									<tr>
										<th>CSV event name</th>
										<th>CSV date</th>
										<th>CSV time</th>
										<th>Rows</th>
										<th>Match to event in our system</th>
										<th>Status</th>
									</tr>
								</thead>
								<tbody>
									{pairs.map((p) => {
										const key = eventPairKey(p.event_name, p.csv_date, p.csv_time || '');
										const selected = mapping[key];
										const isSuggested = selected !== undefined && p.suggested_event_id === selected;
										return (
											<tr key={key}>
												<td>
													<strong>{p.event_name}</strong>
												</td>
												<td style={{ fontFamily: 'monospace' }}>{p.csv_date}</td>
												<td style={{ fontFamily: 'monospace', color: p.csv_time ? '#111827' : '#9ca3af' }}>
													{p.csv_time || '—'}
												</td>
												<td>{p.row_count.toLocaleString()}</td>
												<td>
													<EventCombobox
														suggestions={p.candidates}
														selectedId={selected ?? null}
														onSelect={(eventId) => setPair(p.event_name, p.csv_date, p.csv_time || '', eventId)}
													/>
												</td>
												<td>
													{selected ? (
														isSuggested ? (
															<SuccessPill>Auto-matched</SuccessPill>
														) : (
															<SuccessPill>Manual</SuccessPill>
														)
													) : (
														<ErrorPill>Unmapped</ErrorPill>
													)}
												</td>
											</tr>
										);
									})}
								</tbody>
							</Table>
						</ScrollContainer>
					</>
				)}
			</Card>

			<Footer>
				<Button variant="secondary" onClick={() => history.push(`${pathname}#3`)}>
					← Back
				</Button>
				<div style={{ display: 'flex', gap: 8 }}>
					<Button variant="secondary" onClick={() => refetch()} disabled={isFetching}>
						Re-scan
					</Button>
					<Button onClick={onContinue} disabled={saving || stats.resolved < stats.total || pairs.length === 0}>
						{stats.resolved < stats.total ? `Map all ${stats.total} events to continue` : 'Save and continue →'}
					</Button>
				</div>
			</Footer>
		</>
	);
};
